/**
 * Utility for parsing and humanizing Zapier filter conditions
 * Supports both modern (conditions) and GDPR (filter_criteria) formats
 */

export interface FilterCondition {
  /** Original field/key reference */
  field: string;
  /** Human-readable field name */
  fieldDisplay: string;
  /** Comparison operator */
  operator: string;
  /** Human-readable operator */
  operatorDisplay: string;
  /** Comparison value */
  value: string | number | boolean;
  /** Filter action (continue/stop) */
  action?: 'continue' | 'stop';
  /** Grouping ID for AND/OR logic */
  group?: number;
}

export interface ParsedFilter {
  /** Array of filter conditions */
  conditions: FilterCondition[];
  /** Logic operator (AND/OR) */
  logic: 'AND' | 'OR';
  /** Number of conditions */
  conditionCount: number;
  /** Overall filter action */
  defaultAction: 'continue' | 'stop';
  /** Whether this filter has complex grouping */
  hasGrouping: boolean;
}

/**
 * Operator mapping for human-readable display
 */
const OPERATOR_MAP: Record<string, string> = {
  // Modern format operators
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: 'is greater than',
  less_than: 'is less than',
  '>': 'is greater than',
  '<': 'is less than',
  '>=': 'is greater than or equal to',
  '<=': 'is less than or equal to',
  '==': 'equals',
  '!=': 'does not equal',

  // GDPR format operators
  exact: 'exactly matches',
  icontains: 'contains (case-insensitive)',
  startswith: 'starts with',
  'starts_with': 'starts with',
  endswith: 'ends with',
  'ends_with': 'ends with',
  regex: 'matches regex',
  in: 'is in',
  not_in: 'is not in',
  exists: 'exists',
  not_exists: 'does not exist',
};

/**
 * Extract field display name from variable reference
 * Examples:
 *   "{{steps.1.emailDomain}}" -> "Email Domain (from Step 2)"
 *   "{{66250667__lines[]title}}" -> "Line Item Title"
 *   "email" -> "Email"
 */
function humanizeFieldName(field: string): string {
  // Remove variable braces
  const cleaned = field.replace(/\{\{|\}\}/g, '').trim();

  // Handle steps reference: steps.1.emailDomain
  const stepsMatch = cleaned.match(/steps\.(\d+)\.(.+)/);
  if (stepsMatch) {
    const stepNum = parseInt(stepsMatch[1], 10) + 1; // 0-indexed to 1-indexed
    const fieldName = stepsMatch[2];
    const humanField = camelCaseToTitle(fieldName);
    return `${humanField} (from Step ${stepNum})`;
  }

  // Handle trigger reference: trigger.response.Name
  const triggerMatch = cleaned.match(/trigger\.(.+)/);
  if (triggerMatch) {
    const fieldPath = triggerMatch[1];
    const fieldName = fieldPath.split('.').pop() || fieldPath;
    return `${camelCaseToTitle(fieldName)} (from Trigger)`;
  }

  // Handle GDPR format: 66250667__lines[]title
  const gdprMatch = cleaned.match(/\d+__(.+)/);
  if (gdprMatch) {
    const fieldName = gdprMatch[1].replace(/\[\]/g, ' ').replace(/__/g, ' ');
    return camelCaseToTitle(fieldName);
  }

  // Simple field name
  return camelCaseToTitle(cleaned);
}

/**
 * Convert camelCase or snake_case to Title Case
 */
function camelCaseToTitle(str: string): string {
  // Handle snake_case
  if (str.includes('_')) {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Handle camelCase
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

/**
 * Get human-readable operator display
 */
function humanizeOperator(operator: string): string {
  return OPERATOR_MAP[operator.toLowerCase()] || operator;
}

/**
 * Parse modern format filter (conditions array)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseModernFormat(config: any): ParsedFilter | null {
  if (!config.conditions || !Array.isArray(config.conditions)) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const conditions: FilterCondition[] = config.conditions.map((condition: any) => ({
    field: condition.field || '',
    fieldDisplay: humanizeFieldName(condition.field || ''),
    operator: condition.operator || 'equals',
    operatorDisplay: humanizeOperator(condition.operator || 'equals'),
    value: condition.value,
  }));

  return {
    conditions,
    logic: config.logic?.toUpperCase() === 'OR' ? 'OR' : 'AND',
    conditionCount: conditions.length,
    defaultAction: 'continue',
    hasGrouping: false,
  };
}

/**
 * Parse GDPR format filter (filter_criteria array)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseGDPRFormat(params: any): ParsedFilter | null {
  if (!params.filter_criteria || !Array.isArray(params.filter_criteria)) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const conditions: FilterCondition[] = params.filter_criteria.map((criterion: any) => ({
    field: criterion.key || '',
    fieldDisplay: humanizeFieldName(criterion.key || ''),
    operator: criterion.match || 'equals',
    operatorDisplay: humanizeOperator(criterion.match || 'equals'),
    value: criterion.value,
    action: criterion.action === 'stop' ? 'stop' : 'continue',
    group: criterion.group,
  }));

  // Determine logic based on grouping
  const uniqueGroups = new Set(conditions.map(c => c.group).filter(Boolean));
  const hasGrouping = uniqueGroups.size > 1;

  // Default to AND if no explicit logic
  const logic = hasGrouping ? 'OR' : 'AND';

  // Determine default action from first condition
  const defaultAction = conditions[0]?.action || 'continue';

  return {
    conditions,
    logic,
    conditionCount: conditions.length,
    defaultAction,
    hasGrouping,
  };
}

/**
 * Main parser function - detects format and parses accordingly
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseFilterConditions(config: any, params?: any): ParsedFilter | null {
  // Try modern format first
  if (config && config.conditions) {
    return parseModernFormat(config);
  }

  // Try GDPR format
  if (params && params.filter_criteria) {
    return parseGDPRFormat(params);
  }

  // Check if config itself has filter_criteria
  if (config && config.filter_criteria) {
    return parseGDPRFormat(config);
  }

  return null;
}

/**
 * Generate human-readable filter summary
 * Example: "If Email Domain equals company.com"
 */
export function generateFilterSummary(parsed: ParsedFilter, includeDetails: boolean = true): string {
  if (parsed.conditions.length === 0) {
    return 'No filter conditions';
  }

  if (parsed.conditions.length === 1) {
    const condition = parsed.conditions[0];
    if (!includeDetails) {
      return `Filter: ${condition.fieldDisplay}`;
    }
    return `If ${condition.fieldDisplay} ${condition.operatorDisplay} "${condition.value}"`;
  }

  if (!includeDetails) {
    return `Filter: ${parsed.conditionCount} conditions (${parsed.logic})`;
  }

  // Multiple conditions
  const conditionTexts = parsed.conditions.map(c =>
    `${c.fieldDisplay} ${c.operatorDisplay} "${c.value}"`
  );

  return `If ${conditionTexts.join(` ${parsed.logic} `)}`;
}

/**
 * Generate action description
 */
export function generateActionDescription(parsed: ParsedFilter): string {
  const action = parsed.defaultAction === 'stop' ? 'stop workflow' : 'continue workflow';
  return `Then ${action}`;
}

/**
 * Check if a node has filter conditions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasFilterConditions(config: any, params?: any): boolean {
  if (config?.conditions && Array.isArray(config.conditions) && config.conditions.length > 0) {
    return true;
  }

  if (params?.filter_criteria && Array.isArray(params.filter_criteria) && params.filter_criteria.length > 0) {
    return true;
  }

  if (config?.filter_criteria && Array.isArray(config.filter_criteria) && config.filter_criteria.length > 0) {
    return true;
  }

  return false;
}
